import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDiscountOperations } from "pages/GenericOrderDetailsPage/hooks/useDiscountOperations";
import { useDiscountValidation } from "pages/GenericOrderDetailsPage/hooks/useDiscountValidation";
import { useState } from "react";
import { toast } from "sonner";
import { AmountInput } from "./components/AmountInput";
import { CouponInput } from "./components/CouponInput";
import { DiscountTypeSelector } from "./components/DiscountTypeSelector";
import type {
  Coupon,
  DiscountEditDialogProps,
  DiscountType,
} from "./Types/discount";

const DEFAULT_MIN_ORDER_VALUE = 10000;

export default function DiscountEditDialog({
  couponCode,
  couponCodeID,
  onSave,
  genericOrderId,
  couponinfoDetails,
  amountBeforeTax,
  orderTotal,
  isPaymentApproved = false,
  minOrderValue = DEFAULT_MIN_ORDER_VALUE,
  maxDiscountAmount,
  discountAmount,
  disabled = false,
}: DiscountEditDialogProps) {
  const [open, setOpen] = useState(false);
  const [removeCouponModal, setRemoveCouponModal] = useState<boolean>(false);
  const [discountType, setDiscountType] = useState<DiscountType>("coupon");
  const [code, setCode] = useState(couponCode);
  const [amount, setAmount] = useState("");
  const [couponInfo, setCouponInfo] = useState<Coupon | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    validateCoupon,
    validateDiscountAmount,
    loading: validationLoading,
  } = useDiscountValidation();
  const { applyFlatDiscount, applyCouponDiscount, removeDiscount } =
    useDiscountOperations(genericOrderId);

  // Combined disabled state
  const isButtonDisabled =
    disabled || isPaymentApproved;

  // initialize coupon info when opening (not on every render)
  const handleOpenDialog = () => {
    if (disabled) {
      toast.error("Discount editing is currently disabled");
      return;
    }
    if (isPaymentApproved) {
      toast.error("can not edit discount after payment approval");
      return;
    }
    if (Number(amountBeforeTax) < minOrderValue) {
      toast.error(
        `Minimum order value should be ₹${minOrderValue.toLocaleString()} to apply discount.`
      );
      return;
    }
    if (disabled) {
      toast.error("can not apply discount when balance is zero or negative");
      return;
    }

    if (couponCode && couponinfoDetails) {
      setCouponInfo({
        id: Number(couponinfoDetails?.id),
        coupon_name: couponCode,
        type: couponinfoDetails?.type,
        start_date: null,
        end_date: null,
        eligible_service: "all",
        redemption_limit: 0,
        quantity: couponinfoDetails?.quantity,
        terms_condition: "",
        discount_on: "overall",
      });
      setDiscountType("coupon");
    } else {
      setCouponInfo(null);
      setDiscountType("amount");
    }

    setOpen(true);
  };

  const handleDiscountTypeChange = (type: DiscountType) => {
    setDiscountType(type);
    if (type === "amount") {
      setCode("");
      setCouponInfo(null);
    } else {
      setAmount("");
    }
  };

  const handleSave = async () => {
    setActionLoading(true);
    try {
      if (discountType === "coupon") {
        const validatedCoupon = await validateCoupon(code);
        if (validatedCoupon) {
          const success = await applyCouponDiscount(
            validatedCoupon.id,
            code,
            Number(amountBeforeTax)
          );
          if (success) {
            onSave(code.toUpperCase());
            setOpen(false);
          } else {
            setCode("");
          }
        }
      } else {
        const discountAmount = parseFloat(amount);
        const maxDiscount = maxDiscountAmount || orderTotal;
        if (validateDiscountAmount(discountAmount, maxDiscount)) {
          const success = await applyFlatDiscount(discountAmount);
          if (success) {
            onSave("", discountAmount);
            setOpen(false);
            setAmount("");
          }
        }
      }
    } catch (error) {
      console.error("Error saving discount:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setActionLoading(false);
    }
  };

  const isFormValid = () => {
    if (discountType === "coupon") {
      return code?.trim().length >= 3;
    }
    if (!amount) return false;
    const discountAmount = parseFloat(amount);
    const maxDiscount = maxDiscountAmount || orderTotal;
    return (
      !isNaN(discountAmount) &&
      discountAmount > 0 &&
      discountAmount <= maxDiscount
    );
  };

  const isLoading = validationLoading || actionLoading;

  const handleRemoveCoupon = async () => {
    try {
      const success = await removeDiscount();
      if (success) {
        onSave("", 0);
        setCode("");
        setCouponInfo(null);
      }
    } catch (err) {
      toast.error("Failed to remove discount");
    }
  };

  const handleButtonClick = () => {
    if (disabled) {
      return;
    }

    if (couponCode || (discountAmount || 0) > 0) {
      setRemoveCouponModal(true);
    } else {
      handleOpenDialog();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleButtonClick}
        className={`flex items-center bg-[#E6EEFF] text-[#1B4ED7] text-sm font-medium px-3 py-1.5 rounded-full transition-colors hover:bg-[#D6E4FF] disabled:opacity-50 disabled:cursor-not-allowed ${isButtonDisabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-[#D6E4FF]"
          }`}
        disabled={isButtonDisabled}
      >
        {couponCode || (discountAmount || 0) > 0
          ? couponCode || `Discount Amount: ${discountAmount}`
          : "Add Discount"}

        {couponCode || (discountAmount || 0) > 0 ? (
          <span className="ml-2 text-[#1B4ED7] hover:text-red-500 cursor-pointer">
            ✕
          </span>
        ) : (
          <img
            src="/images/editicon.svg"
            alt="edit icon"
            className="ml-2 w-4 h-4"
          />
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md rounded-xl bg-[#fff] px-0 w-[425px]">
          <DialogHeader className="px-6 border-b border-[#D2D2D2] pb-1">
            <DialogTitle className="text-lg font-bold text-[#333333] text-left">
              Discount
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-6 mt-6 px-6">
            <DiscountTypeSelector
              discountType={discountType}
              onDiscountTypeChange={handleDiscountTypeChange}
            />

            {discountType === "amount" ? (
              <AmountInput
                amount={amount}
                onAmountChange={setAmount}
                orderTotal={maxDiscountAmount || orderTotal}
                onValidationError={toast.error}
              />
            ) : (
              <CouponInput
                code={code}
                onCodeChange={setCode}
                couponInfo={couponInfo}
                originalCode={couponCode}
                disabled={isLoading}
              />
            )}
          </div>

          <DialogFooter className="flex flex-row justify-end gap-3 mt-4 py-5 px-10 bg-[#F9F9F9]">
            <button
              type="button"
              className="px-4 py-2.5 border border-[#165FE3] text-[#165FE3] font-medium rounded-[8px] transition-colors hover:bg-[#F0F4FF] disabled:opacity-50"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={!isFormValid() || isLoading}
              className={`px-6 py-2.5 font-medium rounded-lg transition-colors ${!isFormValid() || isLoading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#165FE3] text-[#fff] hover:bg-[#1942B8]"
                }`}
            >
              {isLoading ? "Processing..." : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* for cancel or remove coupon */}
      <Dialog open={removeCouponModal} onOpenChange={setRemoveCouponModal}>
        <DialogContent className="sm:max-w-md rounded-xl bg-[#fff] px-0 w-[425px]">
          <DialogHeader className="px-6 border-b border-[#D2D2D2] pb-1">
            <DialogTitle className="text-lg font-bold text-[#333333] text-left">
              Remove Coupon
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 mt-4 px-6">
            <p className="text-base font-normal text-[#333333]">
              Are you sure you want to remove the discount/coupon code applied?
            </p>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-3 mt-2 py-3 px-10 bg-[#F9F9F9]">
            <button
              type="button"
              className="px-4 py-2.5 border border-[#165FE3] text-[#165FE3] font-medium rounded-[8px] transition-colors hover:bg-[#F0F4FF]"
              onClick={() => setRemoveCouponModal(false)}
            >
              Close
            </button>
            <button
              onClick={async () => {
                await handleRemoveCoupon();
                setRemoveCouponModal(false);
              }}
              className="px-6 py-2.5 font-medium rounded-lg transition-colors bg-[#165FE3] text-[#fff] hover:bg-[#1942B8]"
            >
              Remove
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
